import { parseIso, timeToMinutes } from './bookingDateTime';


export function toSafeNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function getPackageDurationMins(tariff = {}, fallback = 0) {
  const candidates = [
    tariff.duration_mins,
    tariff.mins,
    tariff.minutes,
    tariff.hours ? Number(tariff.hours) * 60 : null,
    fallback,
  ];

  for (const value of candidates) {
    const numeric = toSafeNumber(value, NaN);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }

  return 0;
}

export function getSeatPricePerHour(seat = {}) {
  return toSafeNumber(seat?.price_per_hour ?? seat?.price_hour ?? seat?.hour_price, 0);
}

export function calculatePackagePriceForSeat(tariff = {}, seat = {}, fallbackDurationMins = 0) {
  const durationMins = getPackageDurationMins(tariff, fallbackDurationMins);
  const pricePerHour = getSeatPricePerHour(seat);
  const base = Math.max(0, (pricePerHour * durationMins) / 60);
  return applyDiscount(base, tariff?.discount_percent ?? tariff?.discount);
}

export function calculatePackageTotal(tariff = {}, seats = [], fallbackDurationMins = 0) {
  if (!Array.isArray(seats) || !seats.length) return 0;

  return Math.max(0, Math.round(seats.reduce((sum, seat) => (
    sum + calculatePackagePriceForSeat(tariff, seat, fallbackDurationMins)
  ), 0)));
}

export function normalizeDiscountPercent(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
}

export function normalizeSeatType(value) {
  const type = String(value || 'pc').trim().toLowerCase();

  if (type === 'console' || type === 'ps' || type === 'playstation') return 'console';
  if (type === 'other') return 'other';

  return 'pc';
}

export function applyDiscount(price, discountPercent) {
  const base = toSafeNumber(price, 0);
  const discount = normalizeDiscountPercent(discountPercent);
  return Math.max(0, Math.round(base * (100 - discount)) / 100);
}

export function parseVisibleDays(raw) {
  const source = raw?.visible_days;

  if (Array.isArray(source)) {
    return source
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item >= 0 && item <= 6);
  }

  if (typeof source === 'string') {
    return source
      .split(',')
      .map((item) => Number(String(item).trim()))
      .filter((item) => Number.isInteger(item) && item >= 0 && item <= 6);
  }

  return [];
}

export function isMinuteInsideWindow(minute, fromTime, toTime) {
  if (!fromTime || !toTime) return true;

  const target = ((minute % (24 * 60)) + (24 * 60)) % (24 * 60);
  const from = timeToMinutes(fromTime);
  const to = timeToMinutes(toTime);

  if (from === to) return true;
  if (from < to) return target >= from && target < to;
  return target >= from || target < to;
}

export function isTariffVisibleForDateTime(tariff, dateValue, timeValue) {
  if (!tariff || !dateValue || !timeValue) return true;

  const date = parseIso(dateValue);
  if (!date) return true;

  const visibleDays = Array.isArray(tariff.visible_days) ? tariff.visible_days : [];
  if (visibleDays.length && !visibleDays.includes(date.getDay())) {
    return false;
  }

  return isMinuteInsideWindow(timeToMinutes(timeValue), tariff.visible_time_from, tariff.visible_time_to);
}

export function normalizePackageTariff(raw, index = 0) {
  const name = String(raw?.name || '').trim();
  const duration = Number(raw?.duration_mins || raw?.mins || (raw?.hours ? Number(raw.hours) * 60 : 0) || 0);
  const lowerName = name.toLowerCase();
  const isNight = String(raw?.kind || raw?.tariff_type || '').toLowerCase() === 'night'
    || lowerName.includes('ноч')
    || (raw?.ends_at_time && String(raw.ends_at_time).startsWith('08:'))
    || (raw?.visible_time_from && raw?.visible_time_to && String(raw.visible_time_from).startsWith('22:') && String(raw.visible_time_to).startsWith('08:'));

  let code = raw?.code || raw?.package_code || null;
  if (!code) {
    if (isNight) code = 'night';
    else if (duration === 180) code = '3h';
    else if (duration === 300) code = '5h';
    else if (duration > 0) code = `pkg-${duration}`;
    else code = `pkg-${raw?.id || index + 1}`;
  }

  const seatType = normalizeSeatType(raw?.seat_type);
  const idPart = raw?.id !== null && raw?.id !== undefined && raw?.id !== '' ? `tariff-${raw.id}` : null;
  const discountPercent = normalizeDiscountPercent(raw?.discount_percent ?? raw?.discount);

  return {
    key: idPart || `${String(code)}-${seatType}`,
    id: raw?.id ?? null,
    code: String(code),
    name: name || (code === '3h' ? '3 часа' : code === '5h' ? '5 часов' : code === 'night' ? 'Ночной' : 'Пакет'),
    description: raw?.description || null,
    duration_mins: duration,
    kind: isNight ? 'night' : 'hours',
    visible_time_from: raw?.visible_time_from || raw?.time_from || null,
    visible_time_to: raw?.visible_time_to || raw?.time_to || null,
    visible_days: parseVisibleDays(raw),
    ends_at_time: raw?.ends_at_time || null,
    sort_order: Number(raw?.sort_order || index),
    price: 0,
    price_fixed: 0,
    discount_percent: discountPercent,
    final_price: null,
    seat_type: seatType,
    color: raw?.color || null,
    bg_svg: raw?.bg_svg || null,
  };
}

export function buildPackageTariffs(packageTemplates = [], legacyPackages = []) {
  const baseRaw = packageTemplates.length ? packageTemplates : legacyPackages;
  const normalized = baseRaw.map((item, index) => normalizePackageTariff(item, index));
  const visuals = legacyPackages.map((item, index) => normalizePackageTariff(item, index));

  const matchVisual = (item) => visuals.find((visual) => {
    if (visual.code && item.code && visual.code === item.code) return true;
    if (visual.kind === 'night' && item.kind === 'night') return true;
    if (Number(visual.duration_mins || 0) > 0 && Number(visual.duration_mins || 0) === Number(item.duration_mins || 0)) return true;
    if (visual.name && item.name && visual.name.toLowerCase() === item.name.toLowerCase()) return true;
    return false;
  }) || null;

  const unique = [];
  const seen = new Set();

  normalized
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((item) => {
      if (seen.has(item.key)) return;
      seen.add(item.key);

      const visual = matchVisual(item);
      unique.push({
        ...item,
        id: item.id ?? visual?.id ?? null,
        description: item.description || visual?.description || null,
        color: item.color || visual?.color || null,
        bg_svg: item.bg_svg || visual?.bg_svg || null,
        price: 0,
        price_fixed: 0,
        discount_percent: item.discount_percent || visual?.discount_percent || 0,
        final_price: null,
        seat_type: normalizeSeatType(item.seat_type || visual?.seat_type),
        visible_days: item.visible_days?.length ? item.visible_days : (visual?.visible_days || []),
        ends_at_time: item.ends_at_time || visual?.ends_at_time || null,
        visible_time_from: item.visible_time_from || visual?.visible_time_from || null,
        visible_time_to: item.visible_time_to || visual?.visible_time_to || null,
      });
    });

  return unique;
}
